import { Widget } from "@serenity-is/corelib";

export class ImageViewerEditor extends Widget<any> {
    // register editor using the same key as the generated attribute
    static override[Symbol.typeInfo] = this.registerEditor('VinaSAB.Common.Fields.ImageViewerEditor');

    private root: any;
    private wrapperEl!: HTMLElement;
    private imgEl!: HTMLImageElement;
    private labelEl?: HTMLElement | null;

    constructor(container: any) {
        super(container);
        this.root = container;
        this.buildUi();
    }

    private buildUi(): void {
        const wrapper = document.createElement('div');
        wrapper.className = 'image-viewer-editor';
        wrapper.style.display = 'flex';
        wrapper.style.alignItems = 'center';
        wrapper.style.justifyContent = 'center';
        wrapper.style.width = '100%';
        wrapper.style.height = '200px';
        wrapper.style.background = '#fafafa';
        wrapper.style.border = '1px solid #e6e6e6';
        wrapper.style.borderRadius = '4px';
        wrapper.style.overflow = 'hidden';

        this.imgEl = document.createElement('img');
        this.imgEl.style.maxWidth = '100%';
        this.imgEl.style.maxHeight = '100%';
        this.imgEl.style.objectFit = 'contain';
        this.imgEl.alt = 'image';
        wrapper.appendChild(this.imgEl);

        // append wrapper robustly to container
        try {
            if (this.root instanceof HTMLElement) {
                this.root.appendChild(wrapper);
                this.labelEl = this.findLabelForHost(this.root);
            } else if (this.root && typeof this.root.element === 'object' && this.root.element instanceof HTMLElement) {
                this.root.element.appendChild(wrapper);
                this.labelEl = this.findLabelForHost(this.root.element);
            } else if (this.root && typeof this.root.append === 'function') {
                this.root.append(wrapper);
                if (this.root && typeof this.root.id === 'string') {
                    const el = document.getElementById(this.root.id);
                    if (el) this.labelEl = this.findLabelForHost(el);
                }
            } else if (this.root && typeof this.root.get === 'function') {
                const el = this.root.get(0);
                if (el instanceof HTMLElement) {
                    el.appendChild(wrapper);
                    this.labelEl = this.findLabelForHost(el);
                }
            } else if (this.root && typeof this.root.id === 'string') {
                const el = document.getElementById(this.root.id);
                if (el) {
                    el.appendChild(wrapper);
                    this.labelEl = this.findLabelForHost(el);
                } else {
                    document.body.appendChild(wrapper);
                }
            } else {
                document.body.appendChild(wrapper);
            }
        } catch {
            document.body.appendChild(wrapper);
        }

        this.wrapperEl = wrapper;
        this.setEmpty();
    }

    private findLabelForHost(host: HTMLElement): HTMLElement | null {
        try {
            let el: HTMLElement | null = host;
            while (el) {
                if (el.classList && el.classList.contains('field')) {
                    const label = el.querySelector('label.caption, label') as HTMLElement | null;
                    return label;
                }
                el = el.parentElement;
            }
        } catch { /* ignore */ }
        return null;
    }

    private setEmpty() {
        // hide wrapper when no image
        this.imgEl.src = '';
        this.imgEl.style.display = 'none';
        if (this.wrapperEl) {
            this.wrapperEl.style.display = 'none';
        }
        if (this.labelEl) {
            this.labelEl.style.display = 'none';
        }
    }

    private setImageSrc(src: string | null) {
        if (!src) {
            this.setEmpty();
            return;
        }

        if (typeof src === 'string' && src.startsWith('data:')) {
            this.imgEl.src = src;
        } else {
            this.imgEl.src = '/ImageFullsize/' + encodeURIComponent(src);
        }
        // show wrapper and image
        if (this.wrapperEl) {
            this.wrapperEl.style.display = 'flex';
        }
        if (this.labelEl) {
            this.labelEl.style.display = '';
        }
        this.imgEl.style.display = '';
    }

    // pick first image from value (array, json array, csv or single)
    private parseFirstImage(v: any): string | null {
        if (v == null) return null;
        if (Array.isArray(v) && v.length > 0) return String(v[0]).trim() || null;

        const s = String(v).trim();
        if (!s) return null;

        if (s.startsWith('[') && s.endsWith(']')) {
            try {
                const arr = JSON.parse(s);
                if (Array.isArray(arr) && arr.length > 0) return String(arr[0]).trim() || null;
            } catch { /* fall through */ }
        }

        const parts = s.split(',').map(x => x.trim()).filter(x => x.length > 0);
        if (parts.length > 0) return parts[0];

        return s || null;
    }

    // Serenity editor API: framework will call set_value
    public set_value(v: any): void {
        const img = this.parseFirstImage(v);
        this.setImageSrc(img);
    }

    public get_value(): any {
        // return source path or empty
        if (!this.imgEl || !this.imgEl.src) return '';
        return this.imgEl.src;
    }

    // property accessors for convenience
    public set value(v: any) { this.set_value(v); }
    public get value(): any { return this.get_value(); }
}